import { gql } from '@apollo/client';
import { DOCUMENT_SUMMARY_FIELDS } from './fragments';

// ============================================================================
// Dashboard Queries
// ============================================================================

/**
 * Dashboard statistics — Anzahl ungelesener Belege je Belegtyp (Posteingang).
 * Use fetchPolicy 'cache-and-network' for fresh counts on each visit.
 */
export const DASHBOARD_STATS_QUERY = gql`
  query DashboardStats($allCustomers: Boolean, $customerNumbers: [String!]) {
    dashboardStats(allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      unreadOffers
      unreadOrders
      unreadInvoices
      unreadDeliveryNotes
    }
  }
`;

/**
 * Rechnungssumme (netto) — Rechnungen minus Gutschriften, serverseitig
 * aggregiert. filter.types wird vom Server ignoriert (fixe Semantik);
 * Clients sollten types trotzdem nicht mitsenden, damit der Apollo-Cache
 * nicht pro Typ-Filter fragmentiert.
 */
export const INVOICE_TOTALS_QUERY = gql`
  query InvoiceTotals($filter: DocumentFilterInput, $allCustomers: Boolean, $customerNumbers: [String!]) {
    invoiceTotals(filter: $filter, allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      netInvoiceTotal
      netCreditTotal
      netSum
      invoiceCount
      creditCount
      earliestDocDate
    }
  }
`;

/**
 * Document statistics — counts by type and date range.
 */
export const DOCUMENT_STATS_QUERY = gql`
  query DocumentStats($allCustomers: Boolean, $customerNumbers: [String!]) {
    documentStats(allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      totalDocuments
      byType {
        type
        count
      }
      dateRange {
        earliest
        latest
      }
    }
  }
`;

/**
 * Recent documents for the dashboard activity feed.
 */
export const RECENT_DOCUMENTS_QUERY = gql`
  query RecentDocuments($limit: Int = 5, $allCustomers: Boolean, $customerNumbers: [String!]) {
    recentDocuments(limit: $limit, allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      ...DocumentSummaryFields
    }
  }
  ${DOCUMENT_SUMMARY_FIELDS}
`;

/**
 * Distinct commission values for filter dropdowns.
 * Relatively stable — cache-first is fine.
 */
export const COMMISSIONS_QUERY = gql`
  query Commissions($allCustomers: Boolean, $customerNumbers: [String!]) {
    commissions(allCustomers: $allCustomers, customerNumbers: $customerNumbers)
  }
`;

/**
 * Distinct location codes for filter dropdowns.
 * Relatively stable — cache-first is fine.
 */
export const LOCATION_CODES_QUERY = gql`
  query LocationCodes($allCustomers: Boolean, $customerNumbers: [String!]) {
    locationCodes(allCustomers: $allCustomers, customerNumbers: $customerNumbers)
  }
`;

/**
 * Distinct customers (debitors) for the admin cross-login picker.
 * Returns the full set — not just customers from the most recent documents.
 * Relatively stable — cache-first is fine.
 */
export const CUSTOMERS_QUERY = gql`
  query Customers($allCustomers: Boolean, $customerNumbers: [String!]) {
    customers(allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      customerNumber
      customerName
    }
  }
`;

/**
 * Sales representative for the current customer.
 */
export const SALES_REPRESENTATIVE_QUERY = gql`
  query SalesRepresentative($documentNumber: String, $allCustomers: Boolean, $customerNumbers: [String!]) {
    salesRepresentative(documentNumber: $documentNumber, allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      name
      phone
      email
      photoUrl
      role
      locationCode
    }
  }
`;
